// Spanish strings for the TBS screens.
//
// Kept out of the components for the same reason the storefront keeps its own in
// `src/pages/shared/constants.ts`: the people who correct the wording are not the people who edit
// JSX, and a string in one file is a string they can be shown.

export const CONTACT_TEXTS = {
  title: "Contactos",
  subtitle: "Clientes y proveedores",
  searchPlaceholder: "Buscar por nombre o identificación",
  create: "Nuevo contacto",
  edit: "Editar contacto",
  empty: "No hay contactos todavía.",
  emptySearch: "Ningún contacto coincide con la búsqueda.",
  emptyTrash: "La papelera está vacía.",
  loading: "Cargando contactos…",
  showTrash: "Ver papelera",
  hideTrash: "Ver contactos activos",
  restore: "Restaurar",
  delete: "Eliminar",
  save: "Guardar",
  cancel: "Cancelar",
  created: "Contacto creado.",
  updated: "Contacto actualizado.",
  deleted: "Contacto enviado a la papelera.",
  restored: "Contacto restaurado.",
  walkin: "Consumidor final",
  back: "Volver",
  inTrash: "En la papelera",
  history: "Historial",
  // Stated rather than faked. The history is this contact's invoices, and that endpoint is built by
  // INV-1 in Wave 2 — calling it now would render an error on an otherwise correct page and teach
  // whoever sees it that errors here are normal.
  historyPending: "El historial de facturas estará disponible cuando se habilite la facturación.",
  // The counter default carries invoice history and the POS assumes exactly one exists per company,
  // so the action is not offered rather than offered and refused.
  walkinProtected: "El contacto Consumidor final no se puede eliminar.",
  deleteConfirm: (name: string) =>
    `¿Enviar “${name}” a la papelera? Las facturas existentes lo siguen mostrando.`,
} as const;

export const CONTACT_FIELDS = {
  name: "Nombre",
  kind: "Tipo",
  idType: "Tipo de identificación",
  idNumber: "Número de identificación",
  email: "Correo electrónico",
  phone: "Teléfono",
  address: "Dirección",
  none: "Sin identificación",
} as const;

export const KIND_LABELS = {
  client: "Cliente",
  supplier: "Proveedor",
  both: "Cliente y proveedor",
} as const;

/** Shared by every list (`TBS-80`). One sentence, because the alert above it carries the detail. */
export const LIST_TEXTS = {
  loadFailed: "No se pudieron cargar los datos.",
} as const;

export const ITEM_TEXTS = {
  title: "Productos",
  // The grid's two states. `loading` below is the *form*'s, and singular — one product, not a list.
  loadingList: "Cargando productos…",
  emptyList: "No hay productos todavía.",
  create: "Nuevo producto",
  edit: "Editar producto",
  save: "Guardar",
  cancel: "Cancelar",
  loading: "Cargando producto…",
  // Shown on the create form only. New items land in the catalog and are activated deliberately,
  // so the form says so rather than offering a switch the API ignores on create.
  activationOnCreate: "Podrá activarlo una vez guardado.",
  // There is no endpoint that lists categories — `category_id` is a filter and a field, and the
  // frozen surface has no CRUD for it. Stated rather than faked, as CON-1 did with the history.
  categoryPending: "Las categorías se administran en una fase posterior.",
} as const;

export const ITEM_FIELDS = {
  name: "Nombre",
  sku: "Referencia",
  description: "Descripción",
  kind: "Tipo",
  price: "Precio de venta",
  cost: "Costo",
  tax: "Impuesto",
  trackInventory: "Controlar existencias",
  active: "Activo",
  storefrontId: "Id en la tienda en línea",
  none: "Sin impuesto",
  // `sku` is sometimes a barcode and sometimes a hand-written reference in the migrated data
  // (§0.2 #3), so the form says what it is for rather than letting someone assume it scans.
  skuHelp: "Referencia interna. No es el código de barras.",
  storefrontIdHelp: "Vincula este producto con su fila en la tienda en línea.",
} as const;

export const KIND_ITEM_LABELS = {
  product: "Producto",
  service: "Servicio",
} as const;

export const PAYMENT_TEXTS = {
  title: "Pagos",
  subtitle: "Dinero recibido",
  empty: "No se registraron pagos en esta fecha.",
  loading: "Cargando pagos…",
  date: "Fecha",
  today: "Hoy",
  detail: "Detalle del pago",
  allocations: "Facturas a las que se aplicó",
  // The word the shop uses for money received with nothing yet to apply it to. "Sin aplicar" would
  // read as an error; this is a customer paying ahead, and it is a normal thing to see.
  advance: "Queda a favor del cliente",
  unapplied: "Anticipo",
  noAllocations: "Este pago no se aplicó a ninguna factura.",
  void: "Anular pago",
  // Voiding is not deleting, and the confirmation has to say so — otherwise "anular" reads as
  // "remove this line" and somebody uses it to tidy up a list.
  voidConfirm:
    "¿Anular este pago? El registro se conserva y los saldos de las facturas vuelven a como estaban.",
  voided: "Pago anulado.",
  total: "Total del día",
  close: "Cerrar",
} as const;

export const PAYMENT_METHOD_LABELS = {
  cash: "Efectivo",
  // Separate rows, because the system stores them separately (decision #19): the Phase-1 exit
  // criterion is that Alegra and this system agree to the peso, and one "Tarjeta" total hides the
  // per-method difference a reconciliation exists to find. A *charge* screen may still offer one
  // button; a report may not collapse them.
  card_debit: "Tarjeta débito",
  card_credit: "Tarjeta crédito",
  transfer: "Transferencia",
  other: "Otro",
} as const;

export const PAYMENT_STATUS_LABELS = {
  active: "Activo",
  void: "Anulado",
} as const;

export const SHIFT_TEXTS = {
  title: "Turno de caja",
  terminal: "Caja",
  // The two screens this page is: there is a shift, or there is not.
  noShift: "No hay un turno abierto en esta caja.",
  openTitle: "Abrir turno",
  openingCash: "Base inicial",
  openingCashHelp: "Cuánto efectivo hay en la caja antes de vender.",
  open: "Abrir turno",
  opened: "Turno abierto.",
  openedAt: "Abierto",
  by: "Por",
  closeTitle: "Cerrar turno y hacer arqueo",
  expected: "Debería haber",
  counted: "Contado",
  countedHelp: "Cuente el efectivo de la caja y escriba el total.",
  difference: "Diferencia",
  balanced: "La caja cuadra.",
  // Said in the shop's words. "Faltan" and "sobran" are what a cashier says out loud, and a surplus
  // is not good news — it usually means a sale nobody recorded.
  short: (amount: string) => `Faltan ${amount}.`,
  over: (amount: string) => `Sobran ${amount}.`,
  noteLabel: "Nota",
  noteRequired: "Explique la diferencia para poder cerrar.",
  noteHelp: "Obligatoria cuando la caja no cuadra.",
  close: "Cerrar turno",
  closed: "Turno cerrado.",
  // The alert is out-of-band — an email from the scheduled check — so the page says what it is
  // rather than pretending to be the alarm.
  staleWarning:
    "Este turno lleva más de 24 horas abierto. Un turno sin cerrar se lleva adentro las ventas del día siguiente.",
  cashOnly:
    "Sólo cuenta el efectivo. Las ventas con tarjeta y las transferencias no pasan por la caja.",
} as const;

export const POS_TEXTS = {
  title: "Punto de venta",
  search: "Escanea o busca por nombre / referencia",
  simpleSale: "Venta simple",
  simpleSaleTitle: "Venta simple",
  simpleSaleHelp: "Una reparación, un servicio, o algo sin código de barras.",
  concept: "Concepto",
  amount: "Valor",
  customer: "Cliente",
  walkin: "Consumidor final",
  changeCustomer: "Cambiar cliente",
  searchCustomer: "Buscar por nombre o identificación",
  useWalkin: "Usar Consumidor final",
  emptyCart: "Sin productos. Escanea o busca para empezar.",
  subtotal: "Subtotal",
  tax: "IVA",
  total: "TOTAL",
  charge: "COBRAR",
  charging: "Cobrando…",
  confirm: "Confirmar",
  cancel: "Cancelar",
  tendered: "Con cuánto paga",
  tenderedHelp: "Para calcular el cambio. Se registra el total de la venta, no lo que entregó.",
  change: "Cambio",
  account: "Cuenta",
  accountHelp: "Dónde entra el dinero.",
  products: (n: number) => `${n} producto${n === 1 ? "" : "s"}`,
  newSale: "Nueva venta",
  closeSale: "Cerrar esta venta",
  // The undo window is what makes cashiers trust the system enough to work fast (§8.3.1).
  issued: (number: string) => `Venta ${number} registrada.`,
  undo: "Anular",
  undone: "Venta anulada.",
  stockWarning: (name: string, have: number) =>
    `“${name}” tiene ${have} en existencia y estás vendiendo más. ¿Continuar?`,
  sellAnyway: "Vender igual",
  noShift: "No hay un turno abierto. Ábrelo antes de vender.",
  openShift: "Abrir turno",
  // Aurora resuming is not a fault — §10.4. A red error here trains cashiers to distrust a system
  // that was merely waking up.
  resuming: "Iniciando… reintentando",
  stock: (n: number | null) => (n === null ? "" : `stock ${n}`),
  // The scanner. Its failure mode is silence, which is indistinguishable from "nobody has scanned
  // yet" unless something counts — so the header says which.
  scanner: "Escáner",
  scannerIdle: "Escáner sin lecturas",
  scannerHelp: "Se actualiza con cada lectura. Si no cambia al disparar, revisa el cable.",
  // An unknown code must not cost the other four lines of a five-item sale.
  unknownCode: (code: string) => `Código no encontrado: ${code}`,
  // Names the product rather than the code: the person at the counter is holding the thing, and
  // "no está activo" about a number they cannot see is not something anybody can act on. It says
  // what to do, because the fix is one screen away and belongs to whoever is standing there.
  inactiveCode: (name: string) => `“${name}” no está activo. Actívelo en Productos para venderlo.`,
  catalogRefreshed: (n: number) => `Catálogo actualizado: ${n} productos.`,
  refreshCatalog: "Actualizar catálogo",
  scannerSpeed: "Sensibilidad del escáner (ms)",
  scannerSpeedHelp:
    "Por debajo de este tiempo entre teclas, la entrada se considera una pistola. Los modelos varían.",
  settings: "Ajustes",
} as const;

export const POS_METHOD_LABELS = {
  cash: "Efectivo",
  card_debit: "T. débito",
  card_credit: "T. crédito",
  transfer: "Transferencia",
  other: "Otro",
} as const;

export const INVOICE_TEXTS = {
  title: "Facturas",
  subtitle: "Ventas emitidas",
  from: "Desde",
  to: "Hasta",
  status: "Estado",
  template: "Numeración",
  allStatuses: "Todos",
  today: "Hoy",
  empty: "No hay facturas en este rango.",
  loading: "Cargando facturas…",
  countTile: "Facturas",
  totalTile: "Total del día",
  balanceTile: "Por cobrar",
  cashTile: "Efectivo",
  cardTile: "Tarjeta",
  detail: "Factura",
  lines: "Líneas",
  item: "Producto",
  quantity: "Cant.",
  unitPrice: "Precio",
  lineTotal: "Total",
  subtotal: "Subtotal",
  tax: "IVA",
  total: "Total",
  balance: "Saldo",
  paymentsApplied: "Pagos aplicados",
  noPayments: "Esta factura no tiene pagos aplicados.",
  pdf: "Descargar PDF",
  // The renderer is TBS-26 and does not exist. The server says so honestly and so does this screen —
  // a button that appears to work and silently does nothing is worse than one that explains.
  pdfPending: "El PDF todavía no está disponible.",
  void: "Anular factura",
  // Voiding never deletes and never frees the number, and the confirmation has to say so — DIAN and
  // the Código de Comercio both require the document to survive.
  voidConfirm:
    "¿Anular esta factura? El documento se conserva con su número y las existencias se devuelven.",
  voidReason: "Motivo",
  voided: "Factura anulada.",
  // §5.8: an invoice with active payments cannot be voided until they are.
  voidBlocked: "Anule primero los pagos de esta factura.",
  back: "Volver",
  dian: "DIAN",
  notFound: "Esa factura no existe.",
} as const;

export const RESUME_TEXTS = {
  title: "Resumen diario",
  loading: "Cargando el resumen…",
  subtitle: "El movimiento del día, igual que siempre",
  date: "Fecha",
  cashIn: "Efectivo entra",
  cashOut: "Efectivo sale",
  cashBalance: "Saldo efectivo",
  bankIn: "Banco entra",
  bankOut: "Banco sale",
  bankBalance: "Saldo banco",
  movements: "Movimientos",
  empty: "No hubo movimientos en esta fecha.",
  number: "Número",
  client: "Cliente",
  method: "Medio",
  account: "Cuenta",
  amount: "Valor",
  cash: "Efectivo",
  bank: "Banco",
} as const;

// `/gestion/contabilidad` — the twin of the storefront's screen (`TBS-67`).
//
// Spanish, and deliberately the *same words* the storefront's screen uses where it has one: the
// point of this screen is that somebody reads the two side by side for a while and compares. Labels
// that differ would make them look like different reports before anyone got to the numbers.
export const CONTABILIDAD_TEXTS = {
  title: "Contabilidad",
  subtitle: "La base del día y el movimiento, contra la base de datos de Tiendas Biai",
  date: "Fecha",
  previousDay: "Día anterior",
  nextDay: "Día siguiente",

  baseTitle: "Apertura de caja",
  baseDescription: "Lo que había en caja y en banco al abrir el día.",
  baseNote: "Los valores se escriben en pesos.",
  cashInitial: "Efectivo inicial",
  bankInitial: "Banco inicial",
  totalInitial: "Total inicial",
  notes: "Observaciones",
  edit: "Editar",
  cancel: "Cancelar",
  save: "Guardar",
  open: "Abrir caja",
  saved: "Apertura de caja exitosa",
  saveError: "Algo salió mal durante la apertura de caja",
  staleError: "Alguien más modificó la base. Recargue la página e intente de nuevo.",
  noBase: "Este día todavía no tiene base. Ábrala para ver el saldo final.",

  resumeTitle: "Resumen del día",
  resumeDescription: "Saldo inicial, movimiento y saldo final por cuenta.",
  account: "CUENTA",
  initial: "INICIAL",
  entries: "ENTRADAS",
  exits: "SALIDAS",
  final: "FINAL",
  cash: "Efectivo",
  bank: "Banco",
  total: "Total",

  movementsTitle: "Transacciones",
  movementsDescription: "Cada pago del día, como lo registró Tiendas Biai.",
  loading: "Cargando...",
  noData: "No hay datos disponibles para la fecha seleccionada.",
} as const;

export const DIAN_TEXTS = {
  title: "Pendientes DIAN",
  loading: "Cargando pendientes…",
  subtitle: "Ventas que todavía no tienen su factura electrónica",
  // An empty grid and a broken grid look identical, so the empty state says which in words.
  empty: "Todas las ventas de hoy tienen su factura electrónica.",
  oldest: "La más antigua",
  minutes: (n: number) => (n < 60 ? `${n} min` : `${Math.floor(n / 60)} h ${n % 60} min`),
  pendingFor: "Pendiente hace",
  invoice: "Número interno",
  client: "Cliente",
  total: "Total",
  issue: "Emitir en DIAN",
  // The second exception this page exists for: a CUFE without its signed XML is a document we
  // cannot produce later (plan §11.3.3).
  missingXml: "Emitidas sin XML guardado",
  missingXmlHelp:
    "El XML firmado es el original fiscal y es nuestra obligación conservarlo. El portal de la DIAN no es nuestro archivo.",
  panelTitle: "Emitir en DIAN",
  panelHelp:
    "Copie cada campo al portal, en este orden. La identificación llena sola el resto del comprador.",
  buyer: "Comprador",
  document: "Documento",
  lines: "Líneas",
  fromPortal: "Lo que devuelve el portal",
  prefix: "Prefijo",
  prefixHelp: "El del portal, no el de Alegra.",
  number: "Número",
  cufe: "CUFE",
  cufeHelp: "Cópielo completo; son unos 96 caracteres.",
  issuedAt: "Fecha y hora del portal",
  issuedAtHelp: "La que muestra el portal, no la de ahora.",
  record: "Registrar",
  close: "Cerrar",
  copy: "Copiar",
  copied: "Copiado",
  recorded: "Documento registrado.",
  // Rows past the alert threshold are marked, never hidden: the alarm already fired, and a page
  // that drops what it alerted on teaches people to distrust both.
  stale: "Lleva demasiado tiempo",
} as const;

export const CONFIG_TEXTS = {
  title: "Configuración",
  subtitle: "Lo que una venta necesita para poder emitirse",
  loading: "Cargando la configuración…",
  // The checklist. It is the first thing on the page for a company that has nothing, and stays
  // afterwards as the answer to "what did I already do?".
  checklistTitle: "Primeros pasos",
  checklistPending: "Todavía no se puede vender. Falta:",
  checklistDone: "La configuración está lista para vender.",
  checklistNext: "Cree sus productos para empezar.",
  checklistNextLink: "Ir a productos",
  recommended: "Recomendado",
  // Said once, on the screen where the choice is made, rather than left to a dropdown with two
  // words in it. It is the difference between a POS that can invoice and one that cannot.
  numberingModeHelp:
    "Interna: Tiendas Biai asigna el número de cada venta. Externa: lo asigna el portal " +
    "gratuito de la DIAN y aquí sólo se registra. Sólo una numeración interna puede facturar " +
    "desde el punto de venta.",
  taxes: "Impuestos",
  taxesHelp: "El IVA que llevan los productos. Un producto sin impuesto se vende al 0%.",
  warehouses: "Bodegas",
  warehousesHelp: "Dónde están las existencias. La predeterminada es la que usa cada venta.",
  accounts: "Cajas y cuentas",
  accountsHelp: "Dónde entra el dinero. La caja es efectivo; la cuenta es el banco.",
  templates: "Numeraciones",
  templatesHelp: "La serie de la que salen los números de factura.",
  add: "Agregar",
  edit: "Editar",
  save: "Guardar",
  cancel: "Cancelar",
  saved: "Configuración guardada.",
  default: "Predeterminada",
  inactive: "Inactiva",
  electronic: "Electrónica",
  empty: "Todavía no hay ninguna.",
  // A version conflict is the one error with an action in it, so it gets its own sentence.
  versionConflict: "Alguien más modificó este registro. Recargue la página e intente de nuevo.",
  // Deactivating is offered on numberings alone: their list returns inactive rows, so it can be
  // undone from this screen. On the other three the list filters them out, and a switch that makes
  // a row vanish with no way back is a trapdoor, not a setting.
  deactivateOnlyNumbering: "Sólo las numeraciones se pueden desactivar desde aquí.",
} as const;

export const CONFIG_FIELDS = {
  name: "Nombre",
  ratePct: "Tarifa (%)",
  ratePctHelp: "En porcentaje. Escriba 19, no 0,19.",
  dianCode: "Código DIAN",
  address: "Dirección",
  kind: "Tipo",
  cash: "Caja (efectivo)",
  bank: "Cuenta bancaria",
  accountNo: "Número de cuenta",
  accountNoHelp: "No se muestra por seguridad. Escríbalo sólo si desea cambiarlo.",
  prefix: "Prefijo",
  prefixHelp:
    "Puede ir vacío. El número impreso no identifica la factura; la serie y el número sí.",
  nextNumber: "Próximo número",
  rangeFrom: "Rango desde",
  rangeTo: "Rango hasta",
  dianResolution: "Resolución DIAN",
  dianValidUntil: "Vence",
  isElectronic: "Factura electrónica",
  numberingMode: "Quién asigna el número",
  internal: "Tiendas Biai (interna)",
  external: "Portal de la DIAN (externa)",
  isDefault: "Predeterminada",
  isActive: "Activa",
} as const;

/** What each checklist step is called, and where the fix is. */
export const READINESS_LABELS = {
  warehouse: "Una bodega predeterminada",
  numbering: "Una numeración predeterminada que reparta números",
  account: "Una caja o cuenta donde entre el dinero",
  tax: "Un impuesto",
} as const;

/** The inventory entry screen (`TBS-70`). */
export const INVENTORY_TEXTS = {
  title: "Entrada de inventario",
  subtitle: "Registre existencias que entran, o corrija las que están mal",
  warehouse: "Bodega",
  reason: "Motivo",
  note: "Nota",
  noteHelp: "Opcional. Queda en el libro junto a cada movimiento.",
  search: "Buscar producto por nombre, referencia o código",
  searchHelp: "También puede escanear el código de barras con la pistola.",
  searching: "Buscando…",
  noResults: "Ningún producto coincide.",
  alreadyAdded: "Ya está en la entrada.",
  add: "Agregar",
  remove: "Quitar",
  lines: "Productos",
  noLines: "Agregue al menos un producto.",
  quantity: "Cantidad",
  currentStock: "Existencias",
  unknownStock: "Sin movimientos",
  // Said where the sign is typed, because it is the one thing about this form that is not obvious:
  // an adjustment may subtract, an entry may not.
  negativeHelp: "Un ajuste puede ser negativo. Escriba -3 para descontar tres.",
  submit: "Registrar entrada",
  submitting: "Registrando…",
  loadingWarehouses: "Cargando bodegas…",
  noWarehouses: "Esta empresa no tiene bodegas. Cree una en Configuración.",
  // The result, which is three separate facts and not one "listo".
  done: (lines: number, warehouse: string) =>
    `Se registraron ${lines} ${lines === 1 ? "movimiento" : "movimientos"} en ${warehouse}.`,
  // **Reported, never refused** (§7.5). The shop sells items whose recorded stock is wrong, and 152
  // products in the live account are already negative — so this is a warning beside the line, not an
  // error that undoes anything.
  negativeTitle: "Quedaron existencias en negativo:",
  negativeRow: (name: string, quantity: string) => `${name}: ${quantity}`,
  // A product that does not track inventory records nothing. The endpoint names which ones, and a
  // screen that dropped the list would leave somebody wondering why a quantity did not move.
  skippedTitle: "No se registraron, porque no manejan inventario:",
} as const;

/** What each reason is called, in the order the select offers them. */
export const ENTRY_REASON_LABELS = {
  initial: "Inventario inicial",
  purchase: "Compra",
  adjustment: "Ajuste",
} as const;

/** The movements ledger (`TBS-71`). */
export const MOVEMENT_TEXTS = {
  title: "Movimientos de inventario",
  subtitle: "Todo lo que ha entrado y salido, con su motivo",
  filterWarehouse: "Bodega",
  filterReason: "Motivo",
  filterItem: "Producto",
  allWarehouses: "Todas",
  allReasons: "Todos",
  clearItem: "Quitar el filtro de producto",
  empty: "No hay movimientos con estos filtros.",
  loading: "Cargando movimientos…",
  newEntry: "Registrar entrada",
  previous: "Anterior",
  next: "Siguiente",
  // The ledger is ordered by `occurred_at` and nothing finer: every movement one sale writes shares
  // a transaction-start timestamp, so their order among themselves means nothing. Said on screen so
  // nobody reads a sequence into rows that are simultaneous.
  simultaneous: "Los movimientos de una misma venta comparten la hora: son simultáneos.",
} as const;

export const MOVEMENT_GRID_TEXTS = {
  occurredAt: "Fecha",
  item: "Producto",
  warehouse: "Bodega",
  reason: "Motivo",
  quantity: "Cantidad",
  note: "Nota",
  invoice: "Factura",
  deletedItem: "(producto eliminado)",
} as const;

/** Every reason a row can carry, including the three no screen can post. */
export const MOVEMENT_REASON_LABELS = {
  sale: "Venta",
  void: "Anulación",
  adjustment: "Ajuste",
  transfer: "Traslado",
  purchase: "Compra",
  initial: "Inventario inicial",
} as const;

/** The product's photo (`TBS-75`). */
export const PHOTO_TEXTS = {
  title: "Foto",
  alt: "Foto del producto",
  upload: "Subir foto",
  replace: "Cambiar foto",
  remove: "Quitar",
  uploading: "Subiendo…",
  help: "JPG, PNG o WEBP. Máximo 5 MB. Se guarda al guardar el producto.",
  // A create has no id to sign an upload for, and saving on the user's behalf would leave a product
  // nobody asked for if the upload then failed.
  saveFirst: "Guarde el producto y podrá agregarle una foto.",
  failed: "No se pudo subir la foto.",
  unconfigured: "Este ambiente todavía no tiene dónde guardar fotos.",
} as const;

/** The transfers screen (`TBS-73`). */
export const TRANSFER_TEXTS = {
  title: "Traslado entre bodegas",
  subtitle: "Mueva existencias de una bodega a otra",
  from: "Bodega de origen",
  to: "Bodega de destino",
  // Said where the quantity is typed, because it is the one rule of this form that is not obvious.
  quantityHelp: "Escriba cantidades positivas: la dirección la dan las dos bodegas.",
  submit: "Registrar traslado",
  submitting: "Registrando…",
  ledger: "Ver movimientos",
  done: (count: number, from: string, to: string) =>
    `Se trasladaron ${count} ${count === 1 ? "producto" : "productos"} de ${from} a ${to}.`,
  row: (name: string, from: string, to: string) => `${name}: ${from} en origen, ${to} en destino`,
  // §7.5, and the same rule the entry screen follows: the boxes moved even if the recorded stock
  // was wrong, so this is a warning beside the result and never something that undoes it.
  negativeTitle: "Quedaron existencias en negativo:",
  skippedTitle: "No se trasladaron, porque no manejan inventario:",
} as const;

export const SNACKBAR_AUTO_HIDE_MS = 4000;

/** §8.3.1: the undo link is live for 60 seconds after a sale is issued. */
export const POS_UNDO_WINDOW_MS = 60000;

/** Page size for the contact list. The API caps `limit` at 200. */
export const CONTACTS_PAGE_SIZE = 50;

/** The header and the landing page (`NAV-1`, `TBS-77`). */
export const NAV_TEXTS = {
  brand: "Tiendas Biai",
  home: "Inicio de Tiendas Biai",
  navLabel: "Navegación de Tiendas Biai",
  openMenu: "Abrir el menú",
  title: "Tiendas Biai",
  subtitle: "Administración de la tienda: inventario, ventas, facturas y pagos.",
  // The state of every user today: the Post-Login Action that emits `tbs_tenants` is not in place,
  // so the honest answer is what is missing and who fixes it — not an empty page that looks broken.
  noAccess: "Su usuario todavía no tiene acceso a ninguna empresa.",
  noAccessHelp: "Pida a un administrador que lo agregue.",
  notFound: "Esta página no existe.",
  notFoundHelp: "Revise la dirección, o vuelva al inicio.",
  backHome: "Ir al inicio",
} as const;
